import React, { useEffect, useState } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import StatusRow from "../shared/StatusRow";

function isPromoter(user) {
  return user.profile_type === "Id::Profile::Promoter";
}

function isMedia(user) {
  return user.profile_type === "Id::Profile::MediaProfile";
}

function getEmbeddingMissingReasons(user) {
  const reasons = [];
  if (isPromoter(user) && (!user.hashtag_follow_count || user.hashtag_follow_count === 0)) {
    reasons.push("no hashtags followed");
  }

  if (reasons.length === 0) {
    return "Check the embedding source text section below for details.";
  }

  return `Possible reasons: ${reasons.join(", ")}. Check the embedding source text section below for details.`;
}

function DiagnosticReport({ user }) {
  const [embeddingData, setEmbeddingData] = useState(null);
  const [embeddingLoading, setEmbeddingLoading] = useState(false);

  useEffect(() => {
    if (!user?.profile_id) return;

    const fetchEmbedding = async () => {
      setEmbeddingLoading(true);
      try {
        const baseUrl = globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;
        const headers = new Headers(authenticatedHeadersHash());

        const response = await fetch(
          `${baseUrl}/rest/v1/profile_embeddings?source_id=eq.${user.profile_id}&source_type=eq.${encodeURIComponent(user.profile_type)}`,
          { headers },
        );

        if (response.ok) {
          const data = await response.json();
          setEmbeddingData(data[0] || null);
        }
      } catch {
        // Silently fail - embedding is optional
      } finally {
        setEmbeddingLoading(false);
      }
    };

    fetchEmbedding();
  }, [user?.profile_id, user?.profile_type]);

  if (!user) return null;

  // Use pre-computed ineligibility_reasons from API (computed in SQL)
  const ineligibilityReasons = user.ineligibility_reasons || [];
  const isEligible = ineligibilityReasons.length === 0;

  return (
    <div>
      <IneligibilityReasonsSection reasons={ineligibilityReasons} isEligible={isEligible} />
      <AccountStatusSection user={user} />
      {isPromoter(user) && <PromoterProfileSection user={user} />}
      {isMedia(user) && <MediaProfileSection user={user} />}
      <DailyLimitSection user={user} />
      <EmailActivitySection user={user} />
      <EmbeddingSourceTextSection embedding={embeddingData} loading={embeddingLoading} />
    </div>
  );
}

function AccountStatusSection({ user }) {
  return (
    <div className="mb-4">
      <h5>Account Status</h5>
      <table className="table table-sm">
        <tbody>
          <tr className={user.in_good_standing ? "table-success" : "table-danger"}>
            <td>
              <strong>In Good Standing</strong>
            </td>
            <td className={user.in_good_standing ? "text-success" : "text-danger"}>
              <strong>{user.in_good_standing ? "✓ Yes" : "✗ No"}</strong>
            </td>
          </tr>
          <StatusRow label="Account Enabled" value={user.account_enabled} />
          <StatusRow label="Not Deleted" value={user.not_deleted} />
          <StatusRow label="Legal Agreements Accepted" value={user.accepted_legals} />
          <StatusRow label="Onboarded" value={user.onboarded} />
          <StatusRow label="Not Under Audit" value={user.not_under_audit} />
          <StatusRow label="Interactions Enabled" value={user.interactions_enabled} />
        </tbody>
      </table>
      {user.onboarded_at && (
        <small className="text-muted">Onboarded: {qformat(user.onboarded_at, DATE_FORMATS.timestamp)}</small>
      )}
      {user.disabled_at && (
        <div>
          <small className="text-danger">Disabled: {qformat(user.disabled_at, DATE_FORMATS.timestamp)}</small>
        </div>
      )}
      {user.deleted_at && (
        <div>
          <small className="text-danger">Deleted: {qformat(user.deleted_at, DATE_FORMATS.timestamp)}</small>
        </div>
      )}
      {user.under_audit_at && (
        <div>
          <small className="text-warning">Under Audit: {qformat(user.under_audit_at, DATE_FORMATS.timestamp)}</small>
        </div>
      )}
    </div>
  );
}

function PromoterProfileSection({ user }) {
  return (
    <div className="mb-4">
      <h5>Promoter Profile & Matching</h5>
      <table className="table table-sm">
        <tbody>
          {user.profile_slug && (
            <tr>
              <td>Profile Slug</td>
              <td>
                <a href={`/pr/${user.profile_slug}`} target="_blank" rel="noopener noreferrer">
                  {user.profile_slug}
                </a>
              </td>
            </tr>
          )}
          <tr>
            <td>Promo Entities (Companies/Products)</td>
            <td>{user.promo_entity_count || 0}</td>
          </tr>
          <StatusRow label="Has Embedding (for semantic matching)" value={user.has_embedding} />
          <tr>
            <td>Hashtags Followed</td>
            <td>{user.hashtag_follow_count || 0}</td>
          </tr>
        </tbody>
      </table>
      {!user.has_embedding && (
        <div className="alert alert-warning" role="alert">
          <small>
            <strong>No embedding found.</strong> {getEmbeddingMissingReasons(user)}
          </small>
        </div>
      )}
    </div>
  );
}

function MediaProfileSection({ user }) {
  return (
    <div className="mb-4">
      <h5>Media Profile</h5>
      <table className="table table-sm">
        <tbody>
          {user.profile_slug && (
            <tr>
              <td>Profile Slug</td>
              <td>
                <a href={`/media/${user.profile_slug}`} target="_blank" rel="noopener noreferrer">
                  {user.profile_slug}
                </a>
              </td>
            </tr>
          )}
          <tr>
            <td>Media Outlet</td>
            <td>
              {user.media_outlet_name ? (
                <a href={`/outlets/${user.media_outlet_slug}`} target="_blank" rel="noopener noreferrer">
                  {user.media_outlet_name}
                </a>
              ) : (
                <span className="text-muted">Not associated</span>
              )}
            </td>
          </tr>
          {user.media_job_title && (
            <tr>
              <td>Job Title</td>
              <td>{user.media_job_title}</td>
            </tr>
          )}
          <StatusRow label="Has Embedding (for semantic matching)" value={user.has_embedding} />
        </tbody>
      </table>
      {!user.has_embedding && (
        <div className="alert alert-warning" role="alert">
          <small>
            <strong>No embedding found.</strong> This may affect story idea matching.
          </small>
        </div>
      )}
    </div>
  );
}

function DailyLimitSection({ user }) {
  return (
    <div className="mb-4">
      <h5>Daily Email Limit</h5>
      <table className="table table-sm">
        <tbody>
          <tr>
            <td>Daily Email Limit</td>
            <td>{user.daily_engagement_email_limit ?? "No limit"}</td>
          </tr>
          <tr>
            <td>Emails Sent Today</td>
            <td>
              {user.emails_sent_today || 0}
              {user.daily_engagement_email_limit && ` / ${user.daily_engagement_email_limit}`}
            </td>
          </tr>
          <StatusRow label="At Daily Limit" value={!user.at_daily_limit} invertColors />
        </tbody>
      </table>
    </div>
  );
}

function EmailActivitySection({ user }) {
  return (
    <div className="mb-4">
      <h5>Email Activity</h5>
      <table className="table table-sm">
        <tbody>
          <tr>
            <td>Emails Last 7 Days</td>
            <td>{user.emails_7d || 0}</td>
          </tr>
          <tr>
            <td>Emails Last 30 Days</td>
            <td>{user.emails_30d || 0}</td>
          </tr>
          <tr>
            <td>Total Emails All Time</td>
            <td>{user.emails_all_time || 0}</td>
          </tr>
          <tr>
            <td>Last Email Sent</td>
            <td>{user.last_email_at ? qformat(user.last_email_at, DATE_FORMATS.timestamp) : "Never"}</td>
          </tr>

          {/* Promoter-specific last sent timestamps */}
          {isPromoter(user) && (
            <>
              {user.new_request_email_last_sent_at && (
                <tr>
                  <td>Last New Request Email</td>
                  <td>{qformat(user.new_request_email_last_sent_at, DATE_FORMATS.timestamp)}</td>
                </tr>
              )}
              {user.promoter_digest_email_last_sent_at && (
                <tr>
                  <td>Last Promoter Digest Email</td>
                  <td>{qformat(user.promoter_digest_email_last_sent_at, DATE_FORMATS.timestamp)}</td>
                </tr>
              )}
            </>
          )}

          {/* Media-specific last sent timestamps */}
          {isMedia(user) && (
            <>
              {user.new_pitch_email_last_sent_at && (
                <tr>
                  <td>Last New Pitch Email</td>
                  <td>{qformat(user.new_pitch_email_last_sent_at, DATE_FORMATS.timestamp)}</td>
                </tr>
              )}
              {user.media_new_story_idea_email_last_sent_at && (
                <tr>
                  <td>Last Story Idea Email</td>
                  <td>{qformat(user.media_new_story_idea_email_last_sent_at, DATE_FORMATS.timestamp)}</td>
                </tr>
              )}
              {user.media_request_healthcheck_email_last_sent_at && (
                <tr>
                  <td>Last Healthcheck Email</td>
                  <td>{qformat(user.media_request_healthcheck_email_last_sent_at, DATE_FORMATS.timestamp)}</td>
                </tr>
              )}
              {user.inventory_email_last_sent_at && (
                <tr>
                  <td>Last Inventory Email</td>
                  <td>{qformat(user.inventory_email_last_sent_at, DATE_FORMATS.timestamp)}</td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function IneligibilityReasonsSection({ reasons, isEligible }) {
  if (isEligible) {
    return (
      <div className="alert alert-success" role="alert">
        <strong>User is eligible to receive notification emails!</strong>
      </div>
    );
  }

  return (
    <div className="alert alert-danger" role="alert">
      <h5>Ineligibility Reasons</h5>
      <p>This user cannot receive notification emails for the following reasons:</p>
      <ul>
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

function EmbeddingSourceTextSection({ embedding, loading }) {
  if (loading) {
    return (
      <div className="mb-4">
        <h5>Embedding Source Text</h5>
        <div className="text-muted">
          <i className="fa fa-spinner fa-spin me-2" />
          Loading embedding data...
        </div>
      </div>
    );
  }

  if (!embedding) {
    return (
      <div className="mb-4">
        <h5>Embedding Source Text</h5>
        <div className="text-muted">No embedding found for this profile.</div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <h5>Embedding Source Text</h5>
      <p className="text-muted small mb-2">
        This is the text used to generate the semantic embedding for matching. Last updated:{" "}
        {qformat(embedding.updated_at, DATE_FORMATS.timestamp, "Unknown")}
      </p>
      <div
        className="bg-light p-3 rounded border"
        style={{
          maxHeight: "300px",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          fontFamily: "monospace",
          fontSize: "0.85rem",
        }}
      >
        {embedding.embedding_source_text || "No source text available."}
      </div>
    </div>
  );
}

export default DiagnosticReport;
